import { LitElement, html, css, PropertyValues, nothing } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { TerminalsModel } from '../../../src/models/terminals-model';
import { debounce, deepCopy, DeviationType, formatTooltipTime, getDeviationClass, tooltipText, isOnline, roundToDigits } from '../../../src/utils/helpers';
import '../../voyage-popup/src/voyage-popup';
import './voyage-location-input';
import '@maersk-global/mds-components-core/mc-tag'
import { VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { VoyageLocationInput } from './voyage-location-input';
import { voyageCursorStyles } from '../../voyage-style/src/voyage-style';
import { verifyETAOverlapWithDepartureTime } from '../../../src/utils/helpers';
import { addPrefillValuesToPassagePlan, areArrivalEtasValid, areDepartureEtdsValid, ARRIVAL_ETA_VALIDATION_ERROR, DEPARTURE_ETD_VALIDATION_ERROR, getPilotPointIndeces, recalcRoutes } from '../../../src/utils/route-utils';
import { ScheduleEntry } from '../../../src/models/types/voyage-schedules-model.types';
import { VoyageSchedulesModel } from '../../../src/models/voyage-schedules-model';
import { VoyageBestPracticeRoutesModel } from '../../../src/models/voyage-best-practice-routes-model';
import { VoyageImoModel } from '../../../src/models/voyage-imo-model';
import { convertToLocalOffline } from '../../../src/utils/offline-utils';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

import { until } from 'lit/directives/until.js';
import { VoyageRoutingModel, WeatherLimits } from '../../../src/models/voyage-routing-model';
import { LoadstarModel, LoadstarValues, decodeLoadstarSource, encodeLoadstarSource } from '../../../src/models/loadstar-model';
@customElement('voyage-leg-details')
export class VoyageLegDetails extends LitElement
{
    @property({type: String, attribute: "schedule-id"}) scheduleId: string = "";
    @property({type: Boolean}) shown: boolean = false;
    @property({type: Boolean}) imminenetRiskOn: boolean = false;
    private _triggerBprJob: boolean = false;
    private _showRollAngle: boolean = false;

    static get styles()
    {
        return [
            voyageCursorStyles,
            css`
            :host {
                font-family: var(--component-paragraph-ff, serif);
            }

            #container {
                display: flex;
                flex-direction: column;
                overflow: auto;
                gap: 30px;
                padding: 0 20px 20px 20px;
            }

            .row {
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                gap: 20px;
            }

            #topContainer {
                position: relative;
                box-sizing: border-box;
                border-bottom: 1px solid var(--neutrals-grey-07);
                border-top: 1px solid var(--neutrals-grey-07);
            }

            .row {
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
            }
            .row > .inputAndHeader {
                width: 140px;
                margin: 0 8px;
            }
            .row > .inputAndHeader:first-child {
                margin-left: 0;
            }
            .row > .inputAndHeader:last-child {
                margin-right: 0;
            }

            .power {
                width: 17.5rem;
            }

            .positionAndTime {
                display: flex;
                flex-direction: row;
                padding: 30px 15px;
                gap: 16px;
            }

            .space-between{
                display: flex;
                justify-content: space-between;
            }

            .positionAndTime[hidden] {
                display: none;
            }

            .positionAndTime:first-child {
                border: none;
            }

            .positionAndTime[pad-right] {
                padding-right: 28px;
            }

            .positionAndTime[pad-left] {
                padding-left: 28px;
            }

            .positionAndTime[pilot] {
                background-color: var(--neutrals-grey-09);
            }

            .inputAndHeader {
                display: flex;
                flex: 1;
                flex-direction: column;
                font-size: 14px;
                color: var(--neutrals-grey-02);
            }

            .positionAndTime > .inputAndHeader {
                width: 280px;
            }

            .noMargin {
                margin-top: 0px;
            }

            .inputContainer {
                display: flex;
                align-items: center;
                box-sizing: border-box;
                height: 52px;
                border: 1px solid var(--neutrals-grey-06);
                background-color: var(--neutrals-grey-10, white);
                border-radius: 3px;
            }

            .inputContainer:focus-within {
                /* Drop Shadow Medium */
                box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
            }

            .inputContainer[invalid] {
                border: 1px solid var(--voyage-error-red, red);
            }
            .inputContainer[invalid] input::placeholder,
            .inputContainer[invalid] input,
            .validation-text {
                color: var(--voyage-error-red, red);
            }

            .validation-text {
                font-size: 12px;
                margin-top: 4px;
            }

            .inputContainer > input::-webkit-outer-spin-button,
            .inputContainer > input::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }
            .inputContainer > input[type=number] {
                -moz-appearance: textfield;
            }

            .inputContainer > label.input-messure {
                padding-right: 10px;
                margin-bottom: 0;
                color: var(--neutrals-grey-04, grey);
            }
            .inputContainer[invalid] > label.input-messure {
                color: var(--voyage-error-red, red);
            }

            label {
                margin-bottom: 8px;
            }

            input {
                font-family: var(--component-paragraph-ff, serif);
                font-size: 14px;
                box-sizing: border-box;
                width: 100%;
                padding-left: 10px;
                padding-right: 10px;
                border: none;
                outline: none;
                color: var(--neutrals-grey-02);
                background-color: unset;
            }

            input[type="datetime-local"] {
                padding-right: 0px;
            }

            .spacer {
                height: 40px;
            }

            .draftInputs {
                display: flex;
                justify-content: space-between;
                margin-top: 5px;
            }

            .nowButton {
                border: none;
                background: none;
                font-family: MaerskText;
                font-size: 14px;
                padding-right: 5px;
                padding-left: 5px;
                color: var(--primary-blue-05, blue);
            }

            .nowButton:focus {
                outline: 1px auto currentColor;
            }

            #footer {
                width: 100%;
                display: flex;
                justify-content: flex-end;
                border-top: 1px solid var(--neutrals-grey-07);
                align-items: center;
            }

            .footerButtons {
                display: flex;
            }

            .footerButton {
                margin-top: 12px;
                margin-bottom: 12px;
                height: 32px;
                min-width: 80px;
                font-size: 14px;
                font-family: var(--component-paragraph-ff, serif);
                padding: 0px 12px 0px 12px;
                background: none;
                border: none;
                outline: none;
                border-radius: 3px;
            }

            .cancel {
                color: var(--neutrals-grey-02);
                margin-right: 10px;
                border: 1px solid var(--neutrals-grey-06, grey);
            }

            .accept {
                background: var(--primary-blue-05, blue);
                margin-right: 20px;
                color: var(--neutrals-grey-10, white);
            }

            .accept[disabled] {
                background: var(--neutrals-grey-06, grey);
            }

            .utcWarn {
                font-weight: bold;
                color: var(--warning-yellow-7);
            }

            .label {
                border-radius: 100px;
                padding-left: 12px;
                width: fit-content;
                padding-right: 12px;
                padding-top: 8px;
                padding-bottom: 8px;
                display: flex;
                align-items: flex-start;
                gap: 4px;
                margin-top: 4px;
            }

            .deviation {
                background-color: #FCDDE0;
                font-weight: bold;
            }

            .aligned {
                background-color: #DCF6D9;
                font-weight: bold;
            }

            .default {
                background: #EDEDED;
            }

            .lowdeviation {
                background-color: #FFF1C2;
                font-weight: bold;
            }
            
            .time-schedules {
                font-size: 12px;
            }

            .label svg{
                width: 16px;
                height: 16px;
            }
            .last-item {
                flex: 0 1 24%;
                display: flex;
                flex-direction: column;
            }

            .pl-20 {
                padding-left: 20px;
            }

            .pr-20 {
                padding-right: 20px;
            }

            .labelWithTag {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 4px;
            }

            .labelWithTag mc-tag {
                position: relative;
                top: -3px;
            }
            
            .toast {
                display: flex;
                min-width: 250px;
                text-align: center;
                border-radius: 5px;
                padding: 12px;
                z-index: 1;
                font-size: 15px;
                align-items: center;
                transition: opacity 0.5s, visibility 0.5s ease-in-out;
                font-family: MaerskText;
                left: 50%;
                }
                .toast.error {
                    background-color: #f5c4c4;
                }
                message-container {
                    flex-grow: 1;
                    text-align: left;
                    margin-left: 8px;
                    margin-right: 8px;
                }
                .close-icon {
                    cursor: pointer;
                    margin-left: 55px;
                    padding-bottom: 7px;
                    width: 10px;
                    height: 10px;
                }
                .icon {
                    margin-right: 10px;
                    width: 18px;
                    height: 18px;
                }
                .icon.error{
                    color: var(--voyage-error-red);
                    padding-bottom: 5px;
                }
        `];
    }

    render()
    {
        let departurePort = "";
        let arrivalPort = "";
        let depInLocalTime = false;
        let arrInLocalTime = false;
        if (this._scheduleEntry)
        {
            departurePort = TerminalsModel!.getCity(this._scheduleEntry.departureTerminalCode) || this._scheduleEntry.departureTerminalCode;
            arrivalPort = TerminalsModel!.getCity(this._scheduleEntry.arrivalTerminalCode) || this._scheduleEntry.arrivalTerminalCode;

            depInLocalTime = !TerminalsModel!.isPseudoTerminal(this._scheduleEntry.departureTerminalCode);
            arrInLocalTime = !TerminalsModel!.isPseudoTerminal(this._scheduleEntry.arrivalTerminalCode);
        }
        
        const depGSIS = this._scheduleEntry?.departurePortCall?.GSIS;
        const arrGSIS = this._scheduleEntry?.arrivalPortCall?.GSIS;
        const depCurrent = this._scheduleEntry?.departurePortCall?.current;
        const arrCurrent = this._scheduleEntry?.arrivalPortCall?.current;

        let scheduledDeparture = depGSIS?.actuals.departureAt ? depGSIS?.actuals.departureAt : depGSIS?.departureAt ?? null;
        let scheduledArrival = arrGSIS?.actuals.arrivalAt ? arrGSIS?.actuals.arrivalAt : arrGSIS?.arrivalAt ?? null;
        let scheduledPilotDeparture = depCurrent?.actuals.pilotOffAt ? depCurrent?.actuals.pilotOffAt : (isVesselOnCloud ? (depGSIS?.estimatedPilotDropOff ?? null) : (depGSIS?.estimatedpilotdropoff ?? null))
        // Get pilot arrival time based on vessel environment (cloud vs edge)
        let scheduledPilotArrival: string | null = null;
        if (isVesselOnCloud) {
            scheduledPilotArrival = arrCurrent?.actuals?.pilotOnAt || arrGSIS?.estimatedPilotBoarding || null;
        } else {
            scheduledPilotArrival = arrCurrent?.actuals.pilotStationArrivalAt ? arrCurrent?.actuals.pilotStationArrivalAt : arrGSIS?.estimatedpilotboarding ?? null;
        }

        let communicatedDeparture = this._etd !== -1 ? this._etd : this._plan?.etd ?? null;
        let communicatedArrival = this._eta !== -1 ? this._eta : this._plan?.eta ?? null;
        let communicatedPilotDeparture = this._pilotEtd !== -1 ? this._pilotEtd : this._plan?.departurePilotEtd ?? null;
        let communicatedPilotArrival = this._pilotEta !== -1 ? this._pilotEta : this._plan?.arrivalPilotEta ?? null;

        this._showRollAngle = localStorage.getItem('krugerModelAvailable') === 'true' || false;
        return html`
        <voyage-popup ?shown=${this.shown} header-text=${`Edit ${departurePort} > ${arrivalPort} Voyage`} ?loading=${this._loading}
                      @close=${this._onClose} @accept=${this._onAccept} >
            <div id="container">

                ${this._planCreationErrorMessage ? html`
                    <div class="toast error">
                        <span class="icon">${SvgIcons.lightTimesCircle}</span> 
                        <div class="message-container">${this._planCreationErrorMessage}</div>
                    </div> 
                ` : nothing}

                <div id="topContainer">
                    <div class="positionAndTime space-between">
                        <voyage-location-input id="fromInput" label="Departure Terminal" show-current-position
                                               @current-position-click=${ () => this._setETD(Date.now()) }>
                        </voyage-location-input>

                        <voyage-location-input id="toInput" label="Arrival Terminal"></voyage-location-input>
                    </div>

                    <div class="positionAndTime" pilot pl-20 pr-20>
                        <div class="inputAndHeader">
                            <label for="etdInput">ETD Berth <span class=${depInLocalTime ? "" : "utcWarn"}>${depInLocalTime ? "(in local time)" : "(in UTC)"}</span></label>
                            <div class="inputContainer" ?invalid=${!this._etdValid}>
                                <input id="etdInput" type="datetime-local">
                                <button class="nowButton" @click=${ () => this._setETD(Date.now()) }>NOW</button>
                            </div>

                            <div data-test="depts" class="time-schedules label ${getDeviationClass(scheduledDeparture, communicatedDeparture)}">
                                <span>${SvgIcons.lightExclamationCircle}
                                    <voyage-tooltip-proxy
                                    text=${this._getTooltipText(scheduledDeparture, communicatedDeparture) ? tooltipText.hasDeviation : tooltipText.default}
                                    html-content
                                    .positionPriority=${["bottom"]}>
                                    </voyage-tooltip-proxy>
                                </span>
                                ${until(formatTooltipTime(scheduledDeparture, this._scheduleEntry?.departureTerminalCode))}${false ? html` <span class="utcWarn">UTC</span>` : scheduledDeparture ? " LT" : '' } 
                            </div>
                        </div>

                        <div class="inputAndHeader">
                            <label for="pilotEtd">ETD Pilot <span class=${depInLocalTime ? "" : "utcWarn"}>${depInLocalTime ? "(in local time)" : "(in UTC)"}</span></label>
                            <div class="inputContainer" ?invalid=${!this._pilotEtdValid}>
                                <input id="pilotEtd" type="datetime-local"
                                       @input=${ (evt: InputEvent) => this._pilotEtdValid = (<HTMLInputElement>evt.target).value !== "" }>
                                <button class="nowButton"
                                        @click=${ () => {
                                            const input = <HTMLInputElement>this.shadowRoot!.getElementById("pilotEtd");
                                            this._updateInputWithDate(input, Date.now(), this._scheduleEntry!.departureTerminalCode);
                                        }}>
                                        NOW
                                </button>
                            </div>
                            <div data-test="depts" class="time-schedules label ${getDeviationClass(scheduledPilotDeparture, communicatedPilotDeparture)}">
                                <span>${SvgIcons.lightExclamationCircle}
                                    <voyage-tooltip-proxy
                                    text=${this._getTooltipText(scheduledPilotDeparture, communicatedPilotDeparture) ? tooltipText.hasDeviation : tooltipText.default}
                                    html-content
                                    .positionPriority=${["bottom"]}>
                                    </voyage-tooltip-proxy>
                                </span>
                                ${until(formatTooltipTime(scheduledPilotDeparture, this._scheduleEntry?.departureTerminalCode))}${false ? html` <span class="utcWarn">UTC</span>` : scheduledPilotDeparture ? " LT" : '' } 
                            </div>
                            ${this._pilotEtdErrorMessage ? html`<span class="validation-text">${this._pilotEtdErrorMessage}</span>` : nothing}
                        </div>

                        <div class="inputAndHeader">
                            <label for="pilotEta">ETA Pilot <span class=${arrInLocalTime ? "" : "utcWarn"}>${arrInLocalTime ? "(in local time)" : "(in UTC)"}</span></label>
                            <div class="inputContainer" ?invalid=${!this._pilotEtaValid}>
                                <input id="pilotEta" type="datetime-local"
                                       @input=${ (evt: InputEvent) => this._pilotEtaValid = (<HTMLInputElement>evt.target).value !== "" }>
                                <button class="nowButton"
                                        @click=${ () => {
                                            const input = <HTMLInputElement>this.shadowRoot!.getElementById("pilotEta");
                                            this._updateInputWithDate(input, Date.now(), this._scheduleEntry!.arrivalTerminalCode);
                                        }}>
                                        NOW
                                </button>
                            </div>
                            <div data-test="depts" class="time-schedules label ${getDeviationClass(scheduledPilotArrival, communicatedPilotArrival)}">
                                <span>${SvgIcons.lightExclamationCircle}
                                    <voyage-tooltip-proxy
                                    text=${this._getTooltipText(scheduledPilotArrival, communicatedPilotArrival) ? tooltipText.hasDeviation : tooltipText.default}
                                    html-content
                                    .positionPriority=${["bottom"]}>
                                    </voyage-tooltip-proxy>
                                </span>
                                ${until(formatTooltipTime(scheduledPilotArrival, this._scheduleEntry?.arrivalTerminalCode))}${false ? html` <span class="utcWarn">UTC</span>` : scheduledPilotArrival ? " LT" : '' } 
                            </div>
                            ${this._pilotEtaErrorMessage ? html`<span class="validation-text">${this._pilotEtaErrorMessage}</span>` : nothing}
                        </div>

                        <div class="inputAndHeader">
                            <label for="etaInput">ETA Berth <span class=${arrInLocalTime ? "" : "utcWarn"}>${arrInLocalTime ? "(in local time)" : "(in UTC)"}</span></label>
                            <div class="inputContainer" ?invalid=${!this._etaValid}>
                                <input id="etaInput" type="datetime-local">
                                <button class="nowButton" @click=${ () => this._setETA(Date.now()) }>NOW</button>
                            </div>

                            <div data-test="depts" class="time-schedules label ${getDeviationClass(scheduledArrival, communicatedArrival)}">
                                <span>${SvgIcons.lightExclamationCircle}
                                    <voyage-tooltip-proxy
                                    text=${this._getTooltipText(scheduledArrival, communicatedArrival) ? tooltipText.hasDeviation : tooltipText.default}
                                    .positionPriority=${["bottom"]}>
                                    </voyage-tooltip-proxy>
                                </span>
                                ${until(formatTooltipTime(scheduledArrival, this._scheduleEntry?.arrivalTerminalCode))}${false ? html` <span class="utcWarn">UTC</span>` : scheduledArrival ? " LT" : '' } 
                            </div>
                        </div>
                    </div>

                </div>

                <div class="row">
                    <div class="inputAndHeader">
                        <div class="labelWithTag">
                            <label for="foreDraftInput">Fore Draft</label>
                            ${this._isForeDraftFromLoadstar && !this._foreDraftEdited ? html`<mc-tag label="LoadStar" appearance="info" fit="small"></mc-tag>` : nothing}
                        </div>
                        <div class="inputContainer" ?invalid=${!this._foreDraftValid}>
                            <input placeholder="Eg. 11.5" id="foreDraftInput" type="number" min="0" max="20" required
                                   @input=${(e: InputEvent) => { this._foreDraftEdited = true; }}>
                            <label class="input-messure">m</label>
                        </div>
                    </div>
                    <div class="inputAndHeader">
                        <div class="labelWithTag">
                            <label for="aftDraftInput">Aft Draft</label>
                            ${this._isAftDraftFromLoadstar && !this._aftDraftEdited ? html`<mc-tag label="LoadStar" appearance="info" fit="small"></mc-tag>` : nothing}
                        </div>
                        <div class="inputContainer" ?invalid=${!this._aftDraftValid}>
                            <input placeholder="Eg. 11.5" id="aftDraftInput" type="number" min="0" max="20" required
                                   @input=${(e: InputEvent) => { this._aftDraftEdited = true; }}>
                            <label class="input-messure">m</label>
                        </div>
                    </div>

                    <div class="inputAndHeader">
                        <div class="labelWithTag">
                            <label for="gmtInput">Fluid GM </label>
                            ${this._isFluidGmFromLoadstar && !this._fluidGmEdited ? html`<mc-tag label="LoadStar" appearance="info" fit="small"></mc-tag>` : nothing}
                        </div>
                        <div class="inputContainer" ?invalid=${this._gmtTouched && !this._gmtValid}>
                            <input placeholder="Eg. 2.2" id="gmtInput" type="text" inputmode="decimal" required
                                       @input=${(e: InputEvent) => {
                                           this._fluidGmEdited = true;
                                           this._gmtTouched = true;
                                           const raw = (<HTMLInputElement>e.target).value.replace(',', '.');
                                           this._gmtRawValue = raw;
                                           this._gmt = raw === '' ? NaN : parseFloat(raw);
                                       }}
                                       @blur=${() => { this._gmtTouched = true; this._validateGmFields(); }}>
                                <label class="input-messure">m</label>
                        </div>
                        ${this._gmtTouched && this._gmtErrorMessage ? html`<span class="validation-text">${this._gmtErrorMessage}</span>` : nothing}
                    </div>

                    <div class="inputAndHeader">
                            <div class="labelWithTag">
                                <label for="solidGmInput">Solid GM </label>
                                ${this._isSolidGmFromLoadstar && !this._solidGmEdited ? html`<mc-tag label="LoadStar" appearance="info" fit="small"></mc-tag>` : nothing}
                            </div>
                            <div class="inputContainer" ?invalid=${this._solidGmTouched && !this._solidGmValid}>
                            <input placeholder="Eg. 2.2" id="solidGmInput" type="text" inputmode="decimal" required
                                       @input=${(e: InputEvent) => {
                                           this._solidGmEdited = true;
                                           this._solidGmTouched = true;
                                           const raw = (<HTMLInputElement>e.target).value.replace(',', '.');
                                           this._solidGmRawValue = raw;
                                           this._solidGm = raw === '' ? NaN : parseFloat(raw);
                                       }}
                                       @blur=${() => { this._solidGmTouched = true; this._validateGmFields(); }}>
                                <label class="input-messure">m</label>
                        </div>
                        ${this._solidGmTouched && this._solidGmErrorMessage ? html`<span class="validation-text">${this._solidGmErrorMessage}</span>` : nothing}
                    </div>
                    
                </div>

                <div class="row">
                    ${this._showRollAngle ? html`
                    <div class="inputAndHeader">
                            <div class="labelWithTag">
                                <label for="rollAngleInput">Maximum Roll Angle</label>
                                ${this._isMaxRollAngleFromLoadstar && !this._maxRollAngleEdited ? html`<mc-tag label="LoadStar" appearance="info" fit="small"></mc-tag>` : nothing}
                            </div>
                            <div class="inputContainer" ?invalid=${!this._rollAngleValid}>
                                <input placeholder="Eg. 5" id="rollAngleInput" type="number" step="1" min="0" max=${this._rollAngle ?? 20} required
                                       @input=${(e: InputEvent) => {
                                           this._maxRollAngleEdited = true;
                                           this._rollAngleValid = this._isValidRollAngle((<HTMLInputElement>e.target).valueAsNumber);
                                           this._rollAngle = (<HTMLInputElement>e.target).valueAsNumber;
                                       }}>
                                <label class="input-messure">deg.</label>
                            </div>
                    </div>` : nothing}
                    <div class="inputAndHeader">
                        <div class="labelWithTag">
                            <label for="reefersInput">Live reefers</label>
                            ${this._isReefersFromLoadstar && !this._reefersEdited ? html`<mc-tag label="LoadStar" appearance="info" fit="small"></mc-tag>` : nothing}
                        </div>
                        <div class="inputContainer" ?invalid=${!this._reefersValid}>
                            <input placeholder="Eg. 600" id="reefersInput" type="number" step="1" min="0" max="3000" required
                                   @input=${(e: InputEvent) => { this._reefersEdited = true; this._reefers = (<HTMLInputElement>e.target).valueAsNumber; }}>
                            <label class="input-messure">Cont.</label>
                        </div>
                    </div>
                    ${this._showRollAngle ? nothing : html`
                        <div class="inputAndHeader"></div>
                    `}
                    <div class="inputAndHeader"> </div>
                    <div class="inputAndHeader"></div>
                </div>
                <!-- <div id="bottomContainer" class="power">
                    <div class="inputAndHeader">
                        <label for="maxPowerInput">Max Power</label>
                        <div class="inputContainer">
                            <input placeholder="Eg. 30" id="maxPowerInput" type="number" min="0" max="60" required>
                            <label class="input-messure">W</label>
                        </div>
                    </div>
                </div> Commenting out as feature is still under development --> 
            </div>

            <div slot="footer" id="footer">
                <div class="footerButtons">
                    <button class="footerButton cancel" @click=${ () => this._onClose() }>Cancel</button>
                    <button class="footerButton accept" ?disabled=${!this._isValid} @click=${ () => this._onAccept() }>Next</button>
                </div>
            </div>
        </voyage-popup>
        `;
    }

    firstUpdated()
    {
        const debounceDelay = 250;
        this._fromInput = <VoyageLocationInput>this.shadowRoot!.getElementById('fromInput');
        this._etdInput = <HTMLInputElement>this.shadowRoot!.getElementById('etdInput');
        this._pilotEtdInput = <HTMLInputElement>this.shadowRoot!.getElementById('pilotEtd')

        this._toInput = <VoyageLocationInput>this.shadowRoot!.getElementById('toInput');
        this._etaInput = <HTMLInputElement>this.shadowRoot!.getElementById('etaInput');
        this._pilotEtaInput = <HTMLInputElement>this.shadowRoot!.getElementById('pilotEta')
        const foreDraftInput = <HTMLInputElement>this.shadowRoot!.getElementById('foreDraftInput');
        const aftDraftInput = <HTMLInputElement>this.shadowRoot!.getElementById('aftDraftInput');
        // gmtInput, solidGmInput, reefersInput, and rollAngleInput now use inline @input handlers in the template
        // const maxPowerInput = <HTMLInputElement>this.shadowRoot!.getElementById('maxPowerInput');

        this._fromInput.addEventListener('value-changed', () => this._checkValidity());
        this._etdInput.addEventListener('input', debounce( async () => {
            this._etd = await this._datetimeToUTC(this._etdInput!.value, this._scheduleEntry!.departureTerminalCode);
        }, debounceDelay));
        this._pilotEtdInput?.addEventListener('input', debounce( async () => {
            this._pilotEtd = await this._datetimeToUTC(this._pilotEtdInput!.value, this._scheduleEntry!.departureTerminalCode);
        }, debounceDelay));
        this._toInput.addEventListener('value-changed', () => this._checkValidity());
        this._etaInput.addEventListener('input', debounce( async () => {
            this._eta = await this._datetimeToUTC(this._etaInput!.value, this._scheduleEntry!.arrivalTerminalCode);
        }, debounceDelay));
        this._pilotEtaInput?.addEventListener('input', debounce( async () => {
            this._pilotEta = await this._datetimeToUTC(this._pilotEtaInput!.value, this._scheduleEntry!.arrivalTerminalCode);
        }, debounceDelay));
        foreDraftInput.addEventListener('input', debounce( () => this._foreDraft = foreDraftInput.valueAsNumber, debounceDelay));
        aftDraftInput.addEventListener('input', debounce( () => this._aftDraft = aftDraftInput.valueAsNumber, debounceDelay));
        // gmtInput, solidGmInput, reefersInput, and rollAngleInput now use inline @input handlers in the template
        // maxPowerInput.addEventListener('input', debounce( () => this._maxPower = maxPowerInput.valueAsNumber, debounceDelay));
    }

    updated(changedProperties: PropertyValues)
    {
        let checkValidity = false;
        changedProperties.forEach((oldValue, propName) => {
            switch (propName)
            {
                case "_etd":
                case "_eta":
                    this._isValidEtd();
                    this._isValidEta();
                    this._verifyPilotEtd();
                    this._verifyPilotEta();
                    checkValidity = true;
                    break;

                case "_pilotEtd":
                case "_pilotEta":
                    this._verifyPilotEtd();
                    this._verifyPilotEta();
                    checkValidity = true;
                    break;

                case "_foreDraft":
                    this._foreDraftValid = this._isValidDraft(this._foreDraft);
                    checkValidity = true;
                    break;

                case "_aftDraft":
                    this._aftDraftValid = this._isValidDraft(this._aftDraft);
                    checkValidity = true;
                    break;

                case "_gmt":
                    this._gmtValid = this._isValidGmt(this._gmt);
                    // Cross-validate solid GM since relational rules depend on fluid GM
                    this._solidGmValid = this._isValidSolidGm(this._solidGm);
                    checkValidity = true;
                    break;

                case "_solidGm":
                    this._solidGmValid = this._isValidSolidGm(this._solidGm);
                    checkValidity = true;
                    break;
                
                case "_rollAngle":
                    this._rollAngleValid = this._isValidRollAngle(this._rollAngle);
                    // Update the input field if it exists
                    const rollAngleInput = this.shadowRoot?.getElementById('rollAngleInput') as HTMLInputElement | null;
                    if (rollAngleInput) {
                        rollAngleInput.value = (this._rollAngle !== null && this._rollAngle !== undefined && !isNaN(this._rollAngle)) ? "" + this._rollAngle : '';
                    }
                    checkValidity = true;
                    break;

                case "_reefers":
                    this._reefersValid = this._isValidReefers(this._reefers);
                    checkValidity = true;
                    break;

                case "shown":
                case "scheduleId":
                    if (this.shown)
                    {
                        this._setDataFromScheduleAndPlan();
                    }
                    break;
            }
        });

        if (checkValidity)
        {
            this._checkValidity();
        }
    }

    @property({attribute: false}) private _etd: number = -1;
    @property({attribute: false}) private _etdValid: boolean = false;
    @property({attribute: false}) private _eta: number = -1;
    @property({attribute: false}) private _etaValid: boolean = false;
    @property({attribute: false}) private _foreDraft: number = NaN;
    @property({attribute: false}) private _foreDraftValid: boolean = false;
    @property({attribute: false}) private _aftDraft: number = NaN;
    @property({attribute: false}) private _aftDraftValid: boolean = false;
    @property({attribute: false}) private _gmt: number = NaN;
    @property({attribute: false}) private _gmtValid: boolean = false;
    @property({attribute: false}) private _solidGm: number = NaN;
    @property({attribute: false}) private _solidGmValid: boolean = false;
    @property({attribute: false}) private _reefers: number = NaN;
    @property({attribute: false}) private _reefersValid: boolean = false;
    @property({attribute: false}) private _maxPower: number | undefined = undefined;
    @property({attribute: false}) private _maxPowerValid: boolean = false;
    @property({attribute: false}) private _rollAngle: number | null = NaN;
    @property({attribute: false}) private _rollAngleValid: boolean = false;
    @property({attribute: false}) private maxRollAngle: number | null = null;
    @property({attribute: false}) private _isForeDraftFromLoadstar: boolean = false;
    @property({attribute: false}) private _isAftDraftFromLoadstar: boolean = false;
    @property({attribute: false}) private _isFluidGmFromLoadstar: boolean = false;
    @property({attribute: false}) private _isSolidGmFromLoadstar: boolean = false;
    @property({attribute: false}) private _isReefersFromLoadstar: boolean = false;
    @property({attribute: false}) private _isMaxRollAngleFromLoadstar: boolean = false;
    @property({attribute: false}) private _foreDraftEdited: boolean = false;
    @property({attribute: false}) private _aftDraftEdited: boolean = false;
    @property({attribute: false}) private _fluidGmEdited: boolean = false;
    @property({attribute: false}) private _solidGmEdited: boolean = false;
    @property({attribute: false}) private _reefersEdited: boolean = false;
    @property({attribute: false}) private _maxRollAngleEdited: boolean = false;
    @property({attribute: false}) private _isValid: boolean = false;
    @property({attribute: false}) private _pilotEtd: number = -1;
    @property({attribute: false}) private _pilotEtdValid: boolean = true;
    @property({attribute: false}) private _pilotEta: number = -1;
    @property({attribute: false}) private _pilotEtaValid: boolean = true;
    @property({attribute: false}) private _loading: boolean = true;
    @property({attribute: false}) private _pilotEtdErrorMessage: string = "";
    @property({attribute: false}) private _pilotEtaErrorMessage: string = "";
    @property({attribute: false}) private _gmtErrorMessage: string = "";
    @property({attribute: false}) private _solidGmErrorMessage: string = "";
    @property({attribute: false}) private _planCreationErrorMessage: string = "";
    @property({type: Boolean, attribute: false }) private _departureBerthSourceIsActual: boolean = false;
    @property({type: Boolean, attribute: false}) private _arrivalBerthIsActual: boolean = false;

    private _fromInput: VoyageLocationInput|null = null;
    private _etdInput: HTMLInputElement|null = null;
    private _toInput: VoyageLocationInput|null = null;
    private _etaInput: HTMLInputElement|null = null;
    private _pilotEtdInput: HTMLInputElement|null = null;
    private _pilotEtaInput: HTMLInputElement|null = null;
    private _scheduleEntry: ScheduleEntry|null = null;
    private _schedule: ScheduleEntry[]|null = null;
    private _scheduleIndex: number = -1;
    private _plan: VoyagePlan|null = null;
    private _pilotPointIndices: number[] = [];
    private _gmtRawValue: string = '';
    private _solidGmRawValue: string = '';
    private _gmtTouched: boolean = false;
    private _solidGmTouched: boolean = false;
    private _loadstarValues: LoadstarValues | null = null;

    private async _setDataFromScheduleAndPlan()
    {
        if (!this.scheduleId) return;
        this._loading = true;
        this._planCreationErrorMessage = "";
        this._gmtTouched = false;
        this._solidGmTouched = false;
        this._gmtErrorMessage = "";
        this._solidGmErrorMessage = "";
        this._foreDraftEdited = false;
        this._aftDraftEdited = false;
        this._fluidGmEdited = false;
        this._solidGmEdited = false;
        this._reefersEdited = false;
        this._maxRollAngleEdited = false;

        this._schedule = await VoyageSchedulesModel!.getScheduleFor(VoyageImoModel!.imo!);
        this._schedule = this._schedule!.filter(s => !s.omitted);
        this._scheduleIndex = this._schedule!.findIndex(entry => entry.id === this.scheduleId)!;
        this._scheduleEntry = this._schedule !.find( entry => entry.id === this.scheduleId)!;

        this._etd = this._scheduleEntry!.etd;
        this._eta = this._scheduleEntry!.eta;
        const voyageNumber = this._scheduleEntry.departurePortCall?.GSIS?.departureVoyage || this._scheduleEntry.arrivalPortCall?.GSIS?.departureVoyage;

        this._departureBerthSourceIsActual = !!this._scheduleEntry.departurePortCall?.current.actuals.departureAt;
        this._arrivalBerthIsActual = !!this._scheduleEntry.arrivalPortCall?.current.actuals.arrivalAt

        this._plan = await VesselPlanningModel!.getCurrentPlan(this.scheduleId);

        // Fetch LoadStar values only if vessel exists in LoadStar
        try {
            const vesselExistsInLoadstar = await LoadstarModel?.currentImoLoadstarExists();
            if (vesselExistsInLoadstar) {
                this._loadstarValues = (await LoadstarModel?.getValueFromLoadstar(VoyageImoModel!.imo!, this.scheduleId, voyageNumber)) ?? null;
                console.info("Fetched LoadStar values:", this._loadstarValues);
            } else {
                this._loadstarValues = null;
            }
        } catch (error) {
            console.warn("Failed to fetch LoadStar values:", error);
            this._loadstarValues = null;
        }

        this.maxRollAngle = null;
        if(this._showRollAngle) {
            try {
                const weatherLimits = localStorage.getItem("voyage-routing-model-weather-limits");
                if (weatherLimits) {
                    const { MaxRollAngle } = JSON.parse(weatherLimits) as WeatherLimits;

                    this.maxRollAngle =
                        MaxRollAngle?.warn ??
                        MaxRollAngle?.avoid ??
                        13; // Default fallback
                } else {
                    console.info("No weather limits found in local storage");
                    this.maxRollAngle = 13;
                }
            } catch (e) {
                console.info("Error fetching max roll angle value", e);
                this.maxRollAngle = 13; // Ensure default is set even on error
            }
        }

        let pilotEtdRaw = isVesselOnCloud ? (this._scheduleEntry.departurePortCall?.GSIS?.estimatedPilotDropOff ?? null) : (this._scheduleEntry.departurePortCall?.GSIS?.estimatedpilotdropoff ?? null);
        let pilotEtaRaw = isVesselOnCloud ? (this._scheduleEntry.arrivalPortCall?.GSIS?.estimatedPilotBoarding ?? null) : (this._scheduleEntry.arrivalPortCall?.GSIS?.estimatedpilotboarding ?? null);
        // GSIS gives these as ISO strings; normalize to epoch ms so downstream numeric comparisons don't coerce them to NaN
        let pilotEtd: any = pilotEtdRaw ? new Date(pilotEtdRaw).getTime() : -1;
        let pilotEta: any = pilotEtaRaw ? new Date(pilotEtaRaw).getTime() : -1;
        const departureTerminalCode = this._scheduleEntry!.departureTerminalCode;
        const arrivalTerminalCode = this._scheduleEntry!.arrivalTerminalCode;
        const depTerminal = TerminalsModel!.getTerminal(departureTerminalCode);
        const arrTerminal = TerminalsModel!.getTerminal(arrivalTerminalCode);
        let startPosition : number[] | null = [depTerminal!.longitude, depTerminal!.latitude];
        let endPosition : number[] | null = [ arrTerminal!.longitude, arrTerminal!.latitude ];

        if (this._plan)
        {
            if (this._plan.routes.length)
            {
                const route = this._plan.routes[this._plan.selectedRouteIndex !== -1 ? this._plan.selectedRouteIndex : 0];
                this._pilotPointIndices = getPilotPointIndeces(route);
            }

            const loadstarForeDraft = this._loadstarValues?.fore_draft != null ? roundToDigits(this._loadstarValues.fore_draft, 2) : undefined;
            this._foreDraft = this._plan.foreDraft < 0 ? (loadstarForeDraft ?? NaN) : this._plan.foreDraft;

            const loadstarAftDraft = this._loadstarValues?.aft_draft != null ? roundToDigits(this._loadstarValues.aft_draft, 2) : undefined;
            this._aftDraft = this._plan.aftDraft < 0 ? (loadstarAftDraft ?? NaN) : this._plan.aftDraft;

            const loadstarLiveReefers = this._loadstarValues?.live_reefers;
            const roundedLoadstarLiveReefers = loadstarLiveReefers != null ? Math.ceil(loadstarLiveReefers) : undefined;
            const hasPlanReefers = this._plan.numberOfReefers >= 0;
            this._reefers = hasPlanReefers ? this._plan.numberOfReefers : (roundedLoadstarLiveReefers ?? NaN);

            const loadstarFluidGm = this._loadstarValues?.fluid_gm != null ? roundToDigits(this._loadstarValues.fluid_gm, 2) : undefined;
            this._gmt = this._plan.gmt === null ? (loadstarFluidGm ?? NaN) : this._plan.gmt;

            this._gmtRawValue = isNaN(this._gmt) ? '' : '' + this._gmt;

            const loadstarMaxRollAngle = this._loadstarValues?.max_roll_angle != null ? Math.ceil(this._loadstarValues.max_roll_angle) : undefined;
            this._rollAngle = this._plan.rollAngle ? this._plan.rollAngle : (loadstarMaxRollAngle != null ? Math.ceil(loadstarMaxRollAngle) : (this.maxRollAngle ?? null));

            const loadstarSolidGm = this._loadstarValues?.solid_gm != null ? roundToDigits(this._loadstarValues.solid_gm, 2) : undefined;
            this._solidGm = (this._plan.solidGm === null || this._plan.solidGm === undefined) ? (loadstarSolidGm ?? NaN) : this._plan.solidGm;

            // Restore provenance flags from the persisted bitmask.
            // On first open (mask is null/undefined) fall back to inferring from value availability,
            // since the field is currently displaying the loadstar fallback value.
            const savedSource = decodeLoadstarSource(this._plan.passagePlan.userInput?.loadstarSourceMask);
            this._isForeDraftFromLoadstar = savedSource.foreDraft || (this._plan.foreDraft < 0 && loadstarForeDraft !== undefined);
            this._isAftDraftFromLoadstar = savedSource.aftDraft || (this._plan.aftDraft < 0 && loadstarAftDraft !== undefined);
            this._isReefersFromLoadstar = savedSource.reefers || (!hasPlanReefers && roundedLoadstarLiveReefers !== undefined);
            this._isFluidGmFromLoadstar = savedSource.fluidGm || (this._plan.gmt === null && loadstarFluidGm !== undefined);
            this._isMaxRollAngleFromLoadstar = savedSource.rollAngle || (!this._plan.rollAngle && loadstarMaxRollAngle !== undefined);
            this._isSolidGmFromLoadstar = savedSource.solidGm || ((this._plan.solidGm === null || this._plan.solidGm === undefined) && loadstarSolidGm !== undefined);

            this._solidGmRawValue = isNaN(this._solidGm) ? '' : '' + this._solidGm;
            if (this._plan.passagePlan.reduced_propulsion_power) {
                this._maxPower = this._plan.passagePlan.reduced_propulsion_power.value ?? NaN;
            }
            startPosition = startPosition || this._plan.startPosition;
            pilotEtd = this._pilotCheckCondition(this._plan.departurePilotEtd) ? pilotEtd : this._plan.departurePilotEtd;
            endPosition = endPosition || this._plan.endPosition;
            pilotEta = this._pilotCheckCondition(this._plan.arrivalPilotEta) ? pilotEta : this._plan.arrivalPilotEta;
            this._etd = this._plan.etd !== -1 ? this._plan.etd : this._etd;
            this._eta =  this._plan.eta !== -1 ? this._plan.eta  : this._eta;
            
            if (this._plan.departureTerminalCode !== departureTerminalCode)
            {
                // Departure port changed - reset start position as well
                startPosition = [ depTerminal!.longitude, depTerminal!.latitude ];
            }

            if (this._plan.arrivalTerminalCode !== arrivalTerminalCode && arrTerminal && arrTerminal.latitude && arrTerminal.longitude)
            {
                // Arrival terminal changed - reset end position as well
                endPosition = [ arrTerminal.longitude, arrTerminal.latitude ];
            }
        }
        else
        {
            const loadstarForeDraft = this._loadstarValues?.fore_draft != null ? roundToDigits(this._loadstarValues.fore_draft, 2) : undefined;
            this._foreDraft = loadstarForeDraft ?? NaN;
            this._isForeDraftFromLoadstar = loadstarForeDraft !== undefined;

            const loadstarAftDraft = this._loadstarValues?.aft_draft != null ? roundToDigits(this._loadstarValues.aft_draft, 2) : undefined;
            this._aftDraft = loadstarAftDraft ?? NaN;
            this._isAftDraftFromLoadstar = loadstarAftDraft !== undefined;

            const loadstarLiveReefers = this._loadstarValues?.live_reefers != null ? Math.ceil(this._loadstarValues.live_reefers) : undefined;
            this._reefers = loadstarLiveReefers != null ? Math.ceil(loadstarLiveReefers) : NaN;
            this._isReefersFromLoadstar = loadstarLiveReefers !== undefined;

            const loadstarFluidGm = this._loadstarValues?.fluid_gm != null ? roundToDigits(this._loadstarValues.fluid_gm, 2) : undefined;
            this._gmt = loadstarFluidGm ?? NaN;
            this._isFluidGmFromLoadstar = loadstarFluidGm !== undefined;

            this._gmtRawValue = isNaN(this._gmt) ? '' : '' + this._gmt;

            const loadstarSolidGm = this._loadstarValues?.solid_gm != null ? roundToDigits(this._loadstarValues.solid_gm, 2) : undefined;
            this._solidGm = loadstarSolidGm ?? NaN;
            this._isSolidGmFromLoadstar = loadstarSolidGm !== undefined;

            this._solidGmRawValue = isNaN(this._solidGm) ? '' : '' + this._solidGm;

            const loadstarMaxRollAngle = this._loadstarValues?.max_roll_angle != null ? Math.ceil(this._loadstarValues.max_roll_angle) : undefined;
            const _rawRollAngleNoplan = loadstarMaxRollAngle ?? (this.maxRollAngle ?? null);
            this._rollAngle = _rawRollAngleNoplan != null ? Math.ceil(_rawRollAngleNoplan) : null;
            this._isMaxRollAngleFromLoadstar = loadstarMaxRollAngle !== undefined;

            if (depTerminal && depTerminal.latitude && depTerminal.longitude)
            {
                startPosition = [ depTerminal.longitude, depTerminal.latitude ];
            }
            if (arrTerminal && arrTerminal.latitude && arrTerminal.longitude)
            {
                endPosition = [ arrTerminal!.longitude, arrTerminal!.latitude ];
            }
            // // Get pilot timings (if available)

            // const depPilotInfo = await TerminalsModel!.getTerminalPilotInfo(departureTerminalCode);

            // const arrPilotInfo = await TerminalsModel!.getTerminalPilotInfo(arrivalTerminalCode);

            // if (depPilotInfo.timeToPilotInSeconds)
            // {
            //     pilotEtd = this._etd + depPilotInfo.timeToPilotInSeconds*1000;
            // }

            // if (arrPilotInfo.timeToBerthInSeconds)
            // {
            //     pilotEta = this._eta - arrPilotInfo.timeToBerthInSeconds*1000;
            // }
        }

        const pilotEtdInput = <HTMLInputElement>this.shadowRoot!.getElementById('pilotEtd');
        const pilotEtaInput = <HTMLInputElement>this.shadowRoot!.getElementById('pilotEta');
        const foreDraftInput = <HTMLInputElement>this.shadowRoot!.getElementById('foreDraftInput');
        const aftDraftInput = <HTMLInputElement>this.shadowRoot!.getElementById('aftDraftInput');
        const gmtInput = <HTMLInputElement>this.shadowRoot!.getElementById('gmtInput');
        const solidGmInput = <HTMLInputElement>this.shadowRoot!.getElementById('solidGmInput');
        const reefersInput = <HTMLInputElement>this.shadowRoot!.getElementById('reefersInput');
        const rollAngleInput = <HTMLInputElement>this.shadowRoot!.getElementById('rollAngleInput');
        // const maxPowerInput = <HTMLInputElement>this.shadowRoot!.getElementById('maxPowerInput');

        // DOM may not be ready yet, so wrap the writes in a try-catch and ignore any errors
        try
        {
            const dateUpdates = [];

            this._fromInput!.setFromTerminalIdOrPosition(Object.assign({}, {
                longitude: startPosition ? startPosition[0] : undefined,
                latitude: startPosition ? startPosition[1] : undefined
            }));
            if (this._etd !== -1)
            {
                dateUpdates.push(this._updateInputWithDate(this._etdInput!, this._etd, this._scheduleEntry!.departureTerminalCode));
            }
            else
            {
                this._etdInput!.value = "";
            }
            
            if (pilotEtd !== -1)
            {
                dateUpdates.push(this._updateInputWithDate(pilotEtdInput, pilotEtd, this._scheduleEntry!.departureTerminalCode));
                this._pilotEtd = pilotEtd;
            }
            else
            {
                this._pilotEtd = -1;
                pilotEtdInput.value = "";
            }

            this._toInput!.setFromTerminalIdOrPosition(Object.assign({}, {
                longitude: endPosition ? endPosition[0] : undefined,
                latitude: endPosition ? endPosition[1] : undefined
            }));
            if (this._eta !== -1)
            {
                dateUpdates.push(this._updateInputWithDate(this._etaInput!, this._eta, this._scheduleEntry!.arrivalTerminalCode));
            }
            else
            {
                this._etaInput!.value = "";
            }
            if (pilotEta !== -1)
            {
                dateUpdates.push(this._updateInputWithDate(pilotEtaInput, pilotEta, this._scheduleEntry!.arrivalTerminalCode));
                this._pilotEta = pilotEta;
            }
            else
            {
                this._pilotEta = -1;
                pilotEtaInput.value = "";
            }

            foreDraftInput.value = isNaN(this._foreDraft) ? "" : "" + this._foreDraft;
            aftDraftInput.value = isNaN(this._aftDraft) ? "" : "" + this._aftDraft;
            reefersInput.value = isNaN(this._reefers) ? "" : "" + this._reefers;
            gmtInput.value = isNaN(this._gmt) ? "" : "" + this._gmt;
            if (solidGmInput) {
                solidGmInput.value = isNaN(this._solidGm) ? "" : "" + this._solidGm;
            }
            if (rollAngleInput) {
                rollAngleInput.value = (this._rollAngle !== null && this._rollAngle !== undefined && !isNaN(this._rollAngle)) ? "" + this._rollAngle : '';
            }
            // maxPowerInput.value = this._maxPower !== undefined && !isNaN(this._maxPower) ? "" + this._maxPower : "";
            await Promise.all(dateUpdates);
        }
        catch (e)
        {
            console.log("_setDataFromCurrentPlan: ", e);
        }

        this._gmtTouched = true;
        this._solidGmTouched = true;
        this._validateGmFields();

        this._loading = false;
    }

    private _pilotCheckCondition(val: string | number | null | undefined) {
        if (isVesselOnCloud) {
            return val === -1 || val === null || val === undefined || val === 0 || isNaN(Number(val)) || val === "";
        } else {
            return val === -1;
        }
    }

    private _setETD(time: number)
    {
        this._etd = time;
        this._updateInputWithDate(this._etdInput!, this._etd, this._scheduleEntry!.departureTerminalCode);
    }

    private _setETA(time: number)
    {
        this._eta = time;
        this._updateInputWithDate(this._etaInput!, this._eta, this._scheduleEntry!.arrivalTerminalCode);
    }

    private _isValidEta()
    {
        this._etaInput!.setCustomValidity("");

        if (this._eta <= this._etd) {
            this._etaValid =  false;
            return;
        }

        this._etaValid = this._eta >= 0;
    }

    private _isValidEtd()
    {
        this._etdInput!.setCustomValidity("");

        if (this._eta <= this._etd) {
            this._etdValid =  false;
            return
        }

        this._etdValid =  this._etd >= 0;
    }

    private _verifyPilotEtd() 
    {
        this._pilotEtdErrorMessage = "";
        if (this._pilotEtd < 0)
        {
            this._pilotEtdValid = false;
            return;
        }
    
        const departurePilotExceededArrivalBerth = this._pilotEtd >= this._eta;
        const departurePilotExceededArrivalPilot = this._pilotEta > 0 ? this._pilotEtd >= this._pilotEta : false;
        const departurePilotExceededDepartureBerth = !areDepartureEtdsValid(this._etd, this._pilotEtd);
        if (departurePilotExceededDepartureBerth) {
            this._pilotEtdErrorMessage = DEPARTURE_ETD_VALIDATION_ERROR;
        }
        
        if (departurePilotExceededArrivalBerth || departurePilotExceededArrivalPilot || departurePilotExceededDepartureBerth)
        {
            this._pilotEtdValid = false;
        }
        else
        {
            this._pilotEtdValid = true;
        }
    }

    private _verifyPilotEta()
    {
        this._pilotEtaErrorMessage = "";
        if (this._pilotEta < 0)
        {
            this._pilotEtaValid = false;
            return;
        }
        let arrivalPilotExceededArrivalBerth = false;
        const arrivalPilotExceededDeparturePilot = this._pilotEtd > 0 ? this._pilotEta <= this._pilotEtd : false;
        const arrivalPilotExceededDepartureBerth = this._pilotEta <= this._etd;
        arrivalPilotExceededArrivalBerth = !areArrivalEtasValid(this._plan, this._pilotEta, this._eta);
        if (arrivalPilotExceededArrivalBerth) {
            this._pilotEtaErrorMessage = ARRIVAL_ETA_VALIDATION_ERROR;
        }

        if (arrivalPilotExceededArrivalBerth || arrivalPilotExceededDepartureBerth || arrivalPilotExceededDeparturePilot)
        {
            this._pilotEtaValid = false;
        }
        else
        {
            this._pilotEtaValid = true;
        }
    }

    private _isValidDraft(draft: number)
    {
        return draft > 0 && draft <= 20;
    }

    //As per nautical dept. GM cannot be lower that 0,60m or higher than 25m.
    //as this is reflected in damage stability booklet; which is a class approved booklet.
    private _isValidGmt(gmt: number): boolean {
        if (this._gmtRawValue === '') {
            this._gmtErrorMessage = "Fluid GM is required.";
            return false;
        }
        if (isNaN(gmt)) {
            this._gmtErrorMessage = "Please enter a valid number (meters).";
            return false;
        }
        if (gmt < 0.6 || gmt > 25.0) {
            this._gmtErrorMessage = "Fluid GM must be between 0.6 m and 25.0 m.";
            return false;
        }
        this._gmtErrorMessage = "";
        return true;
    }

    private _isValidSolidGm(solidGm: number): boolean {
        if (this._solidGmRawValue === '') {
            this._solidGmErrorMessage = "Solid GM is required.";
            return false;
        }
        if (isNaN(solidGm)) {
            this._solidGmErrorMessage = "Please enter a valid number (meters).";
            return false;
        }
        if (solidGm < 0.6 || solidGm > 25) {
            this._solidGmErrorMessage = "Solid GM must be between 0.6 m and 25.0 m.";
            return false;
        }
        if (!isNaN(this._gmt) && solidGm < this._gmt) {
            this._solidGmErrorMessage = "Solid GM must be greater than or equal to Fluid GM.";
            return false;
        }
        if (!isNaN(this._gmt) && solidGm > this._gmt + 5.0) {
            this._solidGmErrorMessage = "Solid GM must not be more than 5.0 m greater than Fluid GM.";
            return false;
        }
        this._solidGmErrorMessage = "";
        return true;
    }

    private _validateGmFields() {
        this._gmtValid = this._isValidGmt(this._gmt);
        this._solidGmValid = this._isValidSolidGm(this._solidGm);
        this._checkValidity();
    }

    private _isValidRollAngle(rollAngle?: number | null) : boolean
    {
        const maxRollAngle = this.maxRollAngle ?? 20;
        return rollAngle ? !isNaN(rollAngle) && rollAngle >= 5 && (rollAngle <= maxRollAngle) : false;
    }

    private _isValidReefers(reefers: number)
    {
        return reefers >= 0 && reefers <= 3000 && Number.isInteger(reefers);
    }

    private _checkValidity()
    {
        const isPositionValid = !this._fromInput!.invalid && !this._toInput!.invalid;
        const isInputTimeValid = this._eta > this._etd && this._etdValid && this._etaValid && this._pilotEtdValid && this._pilotEtaValid;
        const isMiscValid = this._gmtValid && this._solidGmValid && this._reefersValid && this._foreDraftValid && this._aftDraftValid && (this._rollAngleValid || !this._showRollAngle);
        const isPlanCreationFailed = this._plan?.id === -1 || !!this._planCreationErrorMessage;

        this._isValid = isPositionValid && isInputTimeValid && isMiscValid && !isPlanCreationFailed;
    }

    private _getTooltipText(scheduledTime: number | string | null , communicatedTime: number | string | null){
        if (!scheduledTime || !communicatedTime) return false;

        const checkForDeviation = getDeviationClass(scheduledTime, communicatedTime);
        const hasDeviation = checkForDeviation == DeviationType.Deviation || checkForDeviation == DeviationType.LowDeviation;

        return hasDeviation;
    }

    private async _updateInputWithDate(input: HTMLInputElement, utc: number, locationId?: string)
    {
        let time = new Date(utc).toISOString();
        if (locationId && !TerminalsModel!.isPseudoTerminal(locationId))
        {
            time = (isVesselOnCloud && !isOnline()) ? await convertToLocalOffline(utc, locationId) || "" : await TerminalsModel!.getTerminalLocaltime(locationId, new Date(utc).toISOString());
        }

        // The timestamp is returned with full timezone information (e.g. 2019-07-01T12:00:00.000+01:00), so trim off everything except the date, hours and minutes
        input.value = time.substring(0, 16);
    }

    private async _datetimeToUTC(localDateString: string, locationId?: string)
    {
        // Guard against the full datetime not being input yet
        if (!Date.parse(localDateString))
        {
            return -1;
        }

        // The datetime-local component should not include seconds, so normalize to an ISO-like string (without time zone, as it is unknown as of yet)
        const parsedDateString = localDateString.substring(0, 16) + ':00';

        if (locationId && !TerminalsModel!.isPseudoTerminal(locationId))
        {
            return new Date(await TerminalsModel!.getUTCFromTerminalLocaltime(locationId, parsedDateString)).getTime();
        }
        else
        {
            // Use UTC when location is not available (ie. not a terminal)
            return new Date(parsedDateString + "Z").getTime();
        }
    }

    private _onClose()
    {
        this.dispatchEvent(new Event('cancel'));
    }

    private _extractPlanCreationErrorMessage(err: any): string
    {
        const candidates: Array<unknown> = [
            err?.response?.data?.message,
            err?.response?.data?.error,
            err?.response?.data?.detail,
            err?.response?.data?.title,
            err?.message,
            err?.error,
        ];

        const firstString = candidates.find(value => typeof value === 'string' && value.trim().length > 0) as string | undefined;
        if (firstString)
        {
            return firstString;
        }

        if (Array.isArray(err?.response?.data?.errors))
        {
            const messages = err.response.data.errors
                .map((item: any) => (typeof item?.message === 'string' ? item.message : (typeof item === 'string' ? item : '')))
                .filter((item: string) => item.length > 0);
            if (messages.length)
            {
                return messages.join('; ');
            }
        }

        return "Failed to create initial plan. Please cancel and try again.";
    }

    private async _createPlanFromSchedule()
    {
        try
        {
            const gsisList = await VoyageSchedulesModel!.getScheduleFor(VoyageImoModel!.imo!);
            const gsis = gsisList!.find( entry => entry.id === this.scheduleId)!;
            const plan = await VesselPlanningModel!.createPlan(this.scheduleId);
            plan.departureTerminalCode = gsis.departureTerminalCode;
            plan.arrivalTerminalCode = gsis.arrivalTerminalCode;
            this._planCreationErrorMessage = "";

            return plan;
        }
        catch (err)
        {
            this._planCreationErrorMessage = this._extractPlanCreationErrorMessage(err);
            this._checkValidity();
            throw err;
        }
    }

    private async _onAccept()
    {
        this._verifyPilotEta();
        this._checkValidity();
        if (this._pilotEta >= 0 && this._eta >= 0 && !areArrivalEtasValid(this._plan, this._pilotEta, this._eta)) return;

        this._loading = true;
        this._planCreationErrorMessage = "";

        let plan: VoyagePlan;
        try
        {
            plan = await this._saveChanges();
        }
        catch (err) 
        {
            console.warn("Failed to save changes to plan:", err);
            this._loading = false;
            // If _createPlanFromSchedule already set the error message, keep it;
            // otherwise surface a generic fallback so the banner is always shown.
            if (!this._planCreationErrorMessage)
            {
                this._planCreationErrorMessage = this._extractPlanCreationErrorMessage(err);
            }
            this._checkValidity();
            this.requestUpdate();
            return;
        }

        this._loading = false;

        // Recalculates the Star Routes only when the plan inputs changed or a better Star Route became
        // available; otherwise the plan's Star Routes are kept as they are.
        VoyageBestPracticeRoutesModel!.refreshStarRoutesForAcceptedVoyageDetails(plan, this._triggerBprJob);


        this.dispatchEvent(new CustomEvent('accept-voyage-details', { detail: plan }));
        this._triggerBprJob = false;
    }

    private async _saveChanges(): Promise<VoyagePlan>
    {
         await VoyageRoutingModel!.setActivePlan(this.scheduleId)
        // Update schedule if needed
        if (this._etd !== new Date(this._scheduleEntry!.departureAt).getTime())
        {
            const portCall = deepCopy(this._scheduleEntry!.departurePortCall!.current);
            portCall.departureAt = new Date(this._etd).toISOString();
            portCall.estimatedpilotdropoff = new Date(this._pilotEtd).toISOString();
            await VoyageSchedulesModel!.changePortCall(portCall);
        }
        if (this._eta !== new Date(this._scheduleEntry!.arrivalAt).getTime())
        {
            const portCall = deepCopy(this._scheduleEntry!.arrivalPortCall!.current);
            portCall.arrivalAt = new Date(this._eta).toISOString();
            portCall.estimatedpilotboarding = new Date(this._pilotEta).toISOString();

            verifyETAOverlapWithDepartureTime(this._eta, this._scheduleEntry!, portCall);
            await VoyageSchedulesModel!.changePortCall(portCall);
        }

        let currentPlan = await VesselPlanningModel!.getCurrentPlan(this.scheduleId);
        if (!currentPlan)
        {
            currentPlan = await this._createPlanFromSchedule();
        }

        const plan = deepCopy(currentPlan);

        const oldPlanPilotEta = plan.arrivalPilotEta;

        addPrefillValuesToPassagePlan(plan.passagePlan);
        
        // Update plan details from current user input
        plan.startPosition = [this._fromInput!.getPosition()!.longitude, this._fromInput!.getPosition()!.latitude];
        plan.departureTerminalCode = this._scheduleEntry!.departureTerminalCode;

        plan.endPosition = [this._toInput!.getPosition()!.longitude, this._toInput!.getPosition()!.latitude];
        plan.arrivalTerminalCode = this._scheduleEntry!.arrivalTerminalCode;

        // Pilot station information is optional, so handle missing values gracefully
        let reCalcNeeded = false; // Pilot point ETA change forces a re-calculation 
        const pilotEtdInput = <HTMLInputElement>this.shadowRoot!.getElementById('pilotEtd');
        const pilotEtaInput = <HTMLInputElement>this.shadowRoot!.getElementById('pilotEta');
        const departurePilotEtd = await this._datetimeToUTC(pilotEtdInput.value, this._scheduleEntry!.departureTerminalCode);
        const arrivalPilotEta = await this._datetimeToUTC(pilotEtaInput.value, this._scheduleEntry!.arrivalTerminalCode);

        // Helper function to normalize null and NaN for comparison
        const normalizeValue = (val: any) => (val === null || (typeof val === 'number' && isNaN(val))) ? undefined : val;
        
        // Helper to check if two values are actually different (treating null and NaN as equivalent)
        const hasChanged = (planVal: any, currentVal: any) => normalizeValue(planVal) !== normalizeValue(currentVal);

        if(plan.etd !== this._etd || 
            plan.eta !== this._eta || 
            hasChanged(plan.foreDraft, this._foreDraft) || 
            hasChanged(plan.aftDraft, this._aftDraft) || 
            hasChanged(plan.gmt, this._gmt) || 
            hasChanged(plan.solidGm, this._solidGm) || 
            hasChanged(plan.rollAngle, this._rollAngle) || 
            hasChanged(plan.numberOfReefers, this._reefers) || 
            plan.departurePilotEtd !== departurePilotEtd || 
            plan.arrivalPilotEta !== arrivalPilotEta) {
             this._triggerBprJob = true;
             plan.routes = plan.routes.map(r => ({ ...r, recalcNeeded: true }));
             this.requestUpdate();
         }

        if(hasChanged(plan.rollAngle, this._rollAngle)) {
            reCalcNeeded = true;
        }
        const planMaxPower = plan.passagePlan.reduced_propulsion_power?.value;
        //check so we don't make unnecessary PUT requests without change in data
        const hasChanges = plan.etd !== this._etd || plan.eta !== this._eta || hasChanged(plan.foreDraft, this._foreDraft) || hasChanged(plan.aftDraft, this._aftDraft) || hasChanged(plan.gmt, this._gmt) || hasChanged(plan.solidGm, this._solidGm) || hasChanged(plan.numberOfReefers, this._reefers) || plan.departurePilotEtd !== departurePilotEtd || plan.arrivalPilotEta !== arrivalPilotEta || hasChanged(plan.rollAngle, this._rollAngle);
        
        if(hasChanges) {
            plan.etd = this._etd;
            plan.eta = this._eta;
            plan.foreDraft = this._foreDraft;
            plan.aftDraft = this._aftDraft;
            plan.gmt = this._gmt;
            plan.solidGm = isNaN(this._solidGm) ? null : this._solidGm;
            plan.rollAngle = isNaN(this._rollAngle as number) ? null : this._rollAngle;
            plan.numberOfReefers = this._reefers;
            plan.passagePlan.userInput = {
                ...plan.passagePlan.userInput,
                loadstarSourceMask: encodeLoadstarSource({
                    foreDraft:  this._isForeDraftFromLoadstar && !this._foreDraftEdited,
                    aftDraft:   this._isAftDraftFromLoadstar && !this._aftDraftEdited,
                    fluidGm:    this._isFluidGmFromLoadstar && !this._fluidGmEdited,
                    solidGm:    this._isSolidGmFromLoadstar && !this._solidGmEdited,
                    rollAngle:  this._isMaxRollAngleFromLoadstar && !this._maxRollAngleEdited,
                    reefers:    this._isReefersFromLoadstar && !this._reefersEdited,
                }),
            };
            if(plan.passagePlan.reduced_propulsion_power && this._maxPower){
                plan.passagePlan.reduced_propulsion_power.value = this._maxPower;
                plan.passagePlan.reduced_propulsion_power.source = "vesselVoyage"; //set the source for max power
            }
                
                if (departurePilotEtd !== plan.departurePilotEtd)
                {
                    plan.departurePilotEtd = departurePilotEtd;
                    reCalcNeeded = true;
                }
                if (arrivalPilotEta !== plan.arrivalPilotEta)
                {
                    plan.arrivalPilotEta = arrivalPilotEta;
                    reCalcNeeded = true;
                }

                
                this._updateRoutesPilotEtas(plan);

            // if no changes are made to the plan, we don't need to send it to shore
            // if a recalculation is not required and the plan is approved, we need to send it to shore as there are changes made to the plan
            // if a recalculation is required, we don't need to send it to shore as the plan will be recalculated and sent to shore
            const shouldSendToShore = !reCalcNeeded && plan.status === 'approved';
            await VesselPlanningModel!.setPlan(this.scheduleId, plan, shouldSendToShore);
        }

        if (reCalcNeeded && plan.routes.length > 0)
        {
            await recalcRoutes(plan, undefined, undefined, oldPlanPilotEta);
        }

        if(!this._showRollAngle && plan.rollAngle !== null) {
            // If roll angle is not shown, set it to null
            plan.rollAngle = null;
        }

        return plan;
    }

    private _updateRoutesPilotEtas(plan: VoyagePlan): void
    {
        // Update eta on relevant pilot point waypoints
        for (const route of plan.routes)
        {
            const pilotPoints = route.route_geometry.filter( feature => feature.geometry.type === "Point" && feature.properties.pilotPoint );
            if (pilotPoints.length > 0)
            {
                if (plan.departurePilotEtd > 0)
                {
                    pilotPoints[0].properties.eta = new Date(plan.departurePilotEtd).toISOString();
                }
                else
                {
                    delete pilotPoints[0].properties.eta;
                }
                if (plan.arrivalPilotEta > 0 )
                {
                    pilotPoints[pilotPoints.length-1].properties.eta = new Date(plan.arrivalPilotEta).toISOString();    
                }
                else
                {
                    delete pilotPoints[pilotPoints.length-1].properties.eta;
                }
            }
        }

    }
}
